import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  ClearIcon,
  FilterListIcon,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchIcon,
  Tooltip,
  cn,
} from '@billda/design-system';

interface PageToolbarProps {
  tabs?: React.ReactNode;
  actions?: React.ReactNode;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchMode?: 'filter' | 'inline';
  searchPlaceholder?: string;
  hideFilter?: boolean;
  filterContent?: React.ReactNode;
  filterChips?: React.ReactNode;
  isFilterActive?: boolean;
  'data-testid'?: string;
}

/**
 * PageToolbar — same contract as before, composed from DS atoms rather than
 * the DS PageToolbar molecule: the molecule's filter popover is
 * instant-apply with no consumer-controlled open state, while this toolbar
 * keeps the draft→Apply search popover, the active-filter chips row and
 * `hideFilter`.
 */
export const PageToolbar: React.FC<PageToolbarProps> = ({
  tabs,
  actions,
  searchValue,
  onSearchChange,
  searchMode = 'filter',
  searchPlaceholder = 'Search…',
  hideFilter = false,
  filterContent,
  filterChips,
  isFilterActive,
  'data-testid': testId,
}) => {
  const [filterOpen, setFilterOpen] = useState(false);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (hideFilter) {
      setFilterOpen(false);
    }
  }, [hideFilter]);

  const hasSearch = onSearchChange !== undefined;
  const hasPopoverSearch = hasSearch && searchMode === 'filter';
  const hasFilterPopover = hasPopoverSearch || Boolean(filterContent);
  const isActive =
    (hasPopoverSearch && !!searchValue) || (isFilterActive ?? false);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      if (hasFilterPopover) {
        setDraft(searchValue ?? '');
        setFilterOpen(true);
      }
      return;
    }
    setFilterOpen(false);
  };

  const handleApply = () => {
    onSearchChange?.(draft);
    setFilterOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApply();
    }
  };

  const handleClearSearch = () => {
    onSearchChange?.('');
  };

  return (
    <div className="border-b border-border-default" data-testid={testId}>
      {/* Main toolbar row */}
      <div
        className={cn(
          'flex min-h-9 items-center justify-end gap-2 bg-surface-canvas px-4 py-1',
          searchMode === 'inline' && 'flex-wrap'
        )}
      >
        {tabs ? <div className="min-w-0 flex-1">{tabs}</div> : null}
        {hasSearch && searchMode === 'inline' ? (
          <div className="order-last w-full sm:order-none sm:w-64">
            <Input
              inputSize="sm"
              placeholder={searchPlaceholder}
              value={searchValue ?? ''}
              onChange={event => onSearchChange(event.target.value)}
              aria-label={searchPlaceholder}
              data-testid="inline-search-input"
              startAdornment={
                <SearchIcon
                  className="size-4 shrink-0 text-content-secondary"
                  aria-hidden="true"
                />
              }
              endAdornment={
                searchValue ? (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={handleClearSearch}
                    className="flex shrink-0 cursor-pointer items-center rounded-md p-0.5 text-content-secondary hover:text-content-primary"
                  >
                    <ClearIcon className="size-4" aria-hidden="true" />
                  </button>
                ) : undefined
              }
            />
          </div>
        ) : null}
        {!hideFilter && hasFilterPopover && (
          <Popover open={filterOpen} onOpenChange={handleOpenChange}>
            <Tooltip content="Filter">
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Filter"
                  className={cn(
                    isActive ? 'text-content-accent' : 'text-content-secondary'
                  )}
                >
                  <FilterListIcon className="size-3.5" aria-hidden="true" />
                  <span className="hidden sm:inline">Filter</span>
                </Button>
              </PopoverTrigger>
            </Tooltip>
            <PopoverContent align="end" className="w-75 p-3">
              {hasPopoverSearch && (
                <div className="flex items-center gap-2">
                  <Input
                    autoFocus
                    inputSize="sm"
                    placeholder="Search…"
                    value={draft}
                    onChange={event => {
                      setDraft(event.target.value);
                    }}
                    onKeyDown={handleKeyDown}
                    data-testid="filter-search-input"
                    startAdornment={
                      <SearchIcon
                        className="size-4 shrink-0 text-content-secondary"
                        aria-hidden="true"
                      />
                    }
                    endAdornment={
                      draft ? (
                        <button
                          type="button"
                          aria-label="Clear search"
                          onClick={() => {
                            setDraft('');
                          }}
                          className="flex shrink-0 cursor-pointer items-center rounded-md p-0.5 text-content-secondary hover:text-content-primary"
                        >
                          <ClearIcon className="size-4" aria-hidden="true" />
                        </button>
                      ) : undefined
                    }
                  />
                </div>
              )}
              {filterContent ? (
                <div
                  className={cn(
                    hasPopoverSearch &&
                      'mt-2 border-t border-border-default pt-2'
                  )}
                >
                  {filterContent}
                </div>
              ) : null}
              {hasPopoverSearch && (
                <div className="mt-2 flex justify-end gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleApply}
                    disabled={!draft.trim()}
                  >
                    Apply
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        )}
        {actions}
      </div>

      {/* Active filters row */}
      {isActive && (
        <Box className="flex flex-wrap items-center gap-2 border-t border-border-default px-4 py-1.5">
          {hasPopoverSearch && !!searchValue && (
            <Chip
              label={
                <span className="flex items-center gap-1">
                  <SearchIcon className="size-3.5" aria-hidden="true" />
                  <span className="type-caption font-medium">Search</span>
                  <span className="type-caption text-content-secondary">
                    is
                  </span>
                  <span className="type-caption font-medium">
                    {searchValue}
                  </span>
                </span>
              }
              onDelete={handleClearSearch}
              deleteIcon={<ClearIcon className="size-3.5" aria-hidden="true" />}
            />
          )}
          {filterChips}
          {hasPopoverSearch && !!searchValue && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleClearSearch}
              className="type-caption ml-auto text-content-secondary"
            >
              Clear
            </Button>
          )}
        </Box>
      )}
    </div>
  );
};
